import {
  Filters,
  LocationFilters,
  ScheduleApiResponse,
  NotesResponse,
  NoteThread,
  NoteThreadResponse,
  DownloadUrlResponse,
} from "toptaal-shared"
import apiClient from "../client"
import {
  CreateLocationInput,
  GetClassroomsScheduleParams,
  Location,
  LocationByIdResponse,
  LocationInput,
  PaginatedLocationsResponse,
  CreateLocationResponse,
  LocationDocumentsResponse,
  TurnOffLocationPayload,
} from "../types/locations"
import { snakeCase } from "toptaal-shared"
import { uploadDocument } from "toptaal-shared"
import { DocumentDisposition } from "@/hooks/useFileActions"

export async function getFilteredLocations({
  filters: activeFilters,
  ...rest
}: LocationFilters) {
  return await apiClient
    .post<PaginatedLocationsResponse>("locations/filters", {
      json: {
        ...rest,
        filters: {
          archived: rest.archived,
          ...activeFilters,
        },
      },
    })
    .json()
}

export const getLocationById = async (id: Location["id"]) => {
  return await apiClient.get<LocationByIdResponse>(`locations/${id}`).json()
}

export async function validateLocation(location: LocationInput) {
  return await apiClient
    .post("locations/validate", {
      json: { location },
    })
    .json()
}

export async function createLocation(
  location: CreateLocationInput,
): Promise<CreateLocationResponse> {
  return await apiClient
    .post<CreateLocationResponse>("locations", {
      json: { location },
    })
    .json()
}

export async function updateLocation(
  location: Partial<Location> & { locationId: Location["id"] },
) {
  return await apiClient
    .put(`locations/${location.locationId}`, {
      json: { location },
    })
    .json()
}

export const uploadLocationDocument = async ({
  locationId,
  file,
  type,
  displayFilename,
}: {
  locationId: Location["id"]
  file: File
  type: "contract" | "house_rule" | "wifi"
  displayFilename: string
}) => {
  const endpoint = `locations/${locationId}/documents`

  return uploadDocument(
    { displayFilename, file },
    {
      endpoint,
      additionalPostParams: {
        type: snakeCase(type),
      },
      additionalPatchParams: {
        type,
      },
    },
  )
}

export async function fetchLocationNotes(
  locationId: Location["id"],
  filters: Filters,
) {
  return await apiClient
    .post<NotesResponse>(`locations/${locationId}/notes/filters`, {
      json: filters,
    })
    .json()
}

export async function fetchLocationNoteThread(
  locationId: Location["id"],
  noteThreadId: NoteThread["id"],
) {
  return await apiClient
    .get<NoteThreadResponse>(`locations/${locationId}/notes/${noteThreadId}`)
    .json()
}

export async function getClassroomsSchedule({
  classroomId,
  startDate,
  endDate,
}: GetClassroomsScheduleParams) {
  return await apiClient
    .post<ScheduleApiResponse>("locations/schedule", {
      json: { classroomId, startDate, endDate },
    })
    .json()
}

export async function getLocationDocuments(locationId: Location["id"]) {
  return await apiClient
    .get<LocationDocumentsResponse>(`locations/${locationId}/documents`)
    .json()
}

export async function getLocationDocumentUploadUrl(
  locationId: Location["id"],
  uploadId: number,
  options: { disposition: DocumentDisposition },
) {
  const requestOptions = {
    headers: { disposition: options.disposition },
  }

  return await apiClient
    .get<DownloadUrlResponse>(
      `locations/${locationId}/documents/${uploadId}`,
      requestOptions,
    )
    .json()
}

export async function deleteLocationDocument(
  locationId: Location["id"],
  documentId: number,
) {
  return await apiClient
    .delete(`locations/${locationId}/documents/${documentId}`)
    .json()
}

export async function deleteLocation(id: Location["id"]) {
  return await apiClient.delete(`locations/${id}`).json()
}

export async function archiveLocation(
  id: Location["id"],
  disabledFrom?: string,
) {
  return await apiClient
    .post(`locations/${id}/archive`, {
      json: { disabledFrom: disabledFrom ?? null },
    })
    .json()
}

export async function unarchiveLocation(id: Location["id"]) {
  return await apiClient.post(`locations/${id}/unarchive`).json()
}

export async function disableClassroom({
  locationId,
  classroomId,
  disabledFrom,
}: Omit<TurnOffLocationPayload, "classroomIds"> & {
  classroomId: number
}) {
  await apiClient
    .post(`locations/${locationId}/classrooms/${classroomId}/disable`, {
      json: { disabledFrom: disabledFrom ?? null },
    })
    .json()
}
